import { JSX, useCallback, useState } from 'react';
import { useRouter } from 'next/router';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import Button from '@components/Button';
import Card from '@components/Card';
import Input from '@components/Input';
import OtpInput from '@components/OtpInput';
import Text from '@components/Text';
import useApiClient from '@hooks/useApiClient';
import useAppToastContext from '@hooks/useAppToastContext';
import useApplicationFormStateContext from '@hooks/useApplicationFormStateContext';
import useAuthContext from '@hooks/useAuthContext';
import useSendOtpState from '@hooks/useSendOtpState';
import {
  getCompletedStepsV1,
  getEarliestUnfinishedStepV1,
  toApplicationFormContentV1State,
  toAssociatedApplicationsV1State,
} from '@lib/applicationForm';
import { notify } from '@lib/bugsnag';

const OTP_LENGTH = 6;

const REQUEST_FORM_ID = 'resume-application-form';

const VERIFY_FORM_ID = 'verify-resume-application-form';

function ResumeApplicationPageView(): JSX.Element {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const apiClient = useApiClient();
  const { replace } = useRouter();
  const { onLoadProfile } = useAuthContext();
  const { onLoadFormContent } = useApplicationFormStateContext();
  const { onShowToast } = useAppToastContext();
  const [email, setEmail] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isOtpInvalid, setIsOtpInvalid] = useState(false);
  const {
    onSendOtpFailed,
    onSendOtpFulfilled,
    onSendOtpPending,
    state: { isSendingOtp, hasSentInitialOtp, sendOtpCooldown },
  } = useSendOtpState();
  const hasCooldown = sendOtpCooldown !== null;
  const onValueChange = useCallback((v: string): void => {
    if (v) {
      setIsOtpInvalid(false);
    }
  }, []);

  const verifyOtp = useCallback(
    async (emailValue: string, otpValue: string): Promise<void> => {
      setIsVerifying(true);

      try {
        await apiClient.Auth.verifyResumeApplicationOtp(emailValue, otpValue);
        const {
          data: { profile },
        } = await apiClient.me();
        // Now that the user is authenticated, we can load their profile
        onLoadProfile(profile);
        // 1. Get Form State from Server
        const {
          data: {
            content,
            contentVersion,
            postSubmissionContent,
            program,
            reviewStatus,
            submissionStatus,
            submittedAt,
            agreedTermsAndConditions,
            associatedApplications,
          },
        } = await apiClient.Application.get();

        // 2. Hydrate Local Form State
        if (contentVersion === '2022-03-03') {
          // 3. Hydrate Local Form Step and Completed Steps
          onLoadFormContent({
            completedSteps: getCompletedStepsV1(
              content,
              profile,
              agreedTermsAndConditions,
              associatedApplications
            ),
            content: toApplicationFormContentV1State(content),
            postSubmissionContent,
            program,
            reviewStatus,
            step: getEarliestUnfinishedStepV1(
              content,
              profile,
              submissionStatus,
              agreedTermsAndConditions,
              associatedApplications
            ),
            submittedAt,
            agreedTermsAndConditions,
            associatedApplications: toAssociatedApplicationsV1State(
              associatedApplications
            ),
          });
        }

        // 4. Bring user to the application form
        replace('/');
        setIsVerifying(false);
      } catch (error) {
        setIsVerifying(false);
        // @ts-ignore
        if (error.response?.status === 401) {
          setIsOtpInvalid(true);
        } else {
          notify(error);
          onShowToast({
            description: 'Please refresh this page and try again.',
            title: 'Something went wrong',
            type: 'error',
          });
        }
      }
    },
    [apiClient, onLoadProfile, onLoadFormContent, replace, onShowToast]
  );

  return (
    <Card className="mx-auto mt-8 w-11/12 space-y-6 lg:mt-20 lg:w-4/12">
      <form
        id={REQUEST_FORM_ID}
        onSubmit={async (e) => {
          e.preventDefault();

          if (!executeRecaptcha) {
            onShowToast({
              description: 'Please refresh this page and try again',
              title: 'Something went wrong',
              type: 'error',
            });
            return;
          }

          onSendOtpPending();

          try {
            const recaptchaToken = await executeRecaptcha('LOGIN_REQUEST');
            await apiClient.Auth.requestResumeApplicationOtp(
              email,
              recaptchaToken
            );
            onSendOtpFulfilled();
            onShowToast({
              description:
                'You will receive an email if you previously started an application',
              title: 'Check your inbox',
            });
          } catch (error) {
            notify(error);
            onSendOtpFailed();
          }
        }}
      >
        <div className="space-y-3">
          <Text className="font-semibold" size="heading3">
            Continue My Application
          </Text>
          <Text>Enter your email address to receive a one-time password</Text>
        </div>
        <div className="mt-6">
          <Input
            endAdornment={
              <Button
                disabled={!email || isSendingOtp || hasCooldown}
                form={REQUEST_FORM_ID}
                loading={isSendingOtp}
                type="submit"
                variant="text"
              >
                {hasCooldown ? `Resend (${sendOtpCooldown})` : 'Submit'}
              </Button>
            }
            fullWidth
            label="Email Address"
            onChange={(e): void => setEmail(e.target.value.toLowerCase())}
            placeholder="Enter Email Address"
            required
            type="email"
            value={email}
          />
        </div>
      </form>
      <div>
        <Text as="label" className="mb-2 block text-subheading3 font-bold">
          Email OTP
        </Text>
        <OtpInput
          disabled={!hasSentInitialOtp}
          id={VERIFY_FORM_ID}
          isInvalid={isOtpInvalid}
          numberOfInputs={OTP_LENGTH}
          onSubmit={(otpValue: string): void => {
            verifyOtp(email, otpValue);
          }}
          onValueChange={onValueChange}
        >
          {({ renderInput, value: otpValue }) => (
            <>
              {renderInput()}
              {isOtpInvalid && (
                <Text color="red-100" className="mt-2 font-medium" size="body2">
                  Your email OTP is incorrect. Please try again.
                </Text>
              )}
              <div className="mt-10 flex flex-row justify-end">
                <Button
                  disabled={
                    isSendingOtp ||
                    !hasSentInitialOtp ||
                    !email ||
                    !otpValue ||
                    otpValue.length !== OTP_LENGTH ||
                    isVerifying
                  }
                >
                  Continue
                </Button>
              </div>
            </>
          )}
        </OtpInput>
      </div>
    </Card>
  );
}

export default ResumeApplicationPageView;
